import { CUTTING_SHAPE_ID, GRID_HELPER_ID } from '@/domain/selection/reservedSelectionIds';
import { CUTTING_OUTLINE_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import { EExportRenderingMode } from '@/application/ports/EExportRenderingMode';
import { setBackgroundMaskVisibility } from '@/infrastructure/engine2d/helpers/FabricBackgroundMaskHelper';
import { isMaskImage } from '@/infrastructure/engine2d/overlays/MaskClipService';
import { setCuttingPathVisibility } from '@/infrastructure/engine2d/FabricCallbacks';
import { BACKGROUND_MASK_DEFAULT_OPACITY, CUTTING_PATH_DEFAULT_OPACITY } from '@/infrastructure/engine2d/engine2dConstants';
import type { Canvas, FabricObject } from 'fabric';

type TStripOptions = {
    // Keep the semi-transparent cutting group, the blue outline and
    // the background mask visible — customer-facing preview (BAT).
    // Print / texture strip them all.
    keepCuttingGuides: boolean;
};

const _OPTIONS_BY_MODE: Record<EExportRenderingMode, TStripOptions> = {
    [EExportRenderingMode.Texture]: { keepCuttingGuides: false },
    [EExportRenderingMode.BatPreview]: { keepCuttingGuides: true },
    [EExportRenderingMode.Print]: { keepCuttingGuides: false },
};

// Strip helper overlays from the cloned canvas before it's rendered
// to a texture / blob. The mode selects which overlays survive — the
// mapping to concrete "keep / strip" flags lives in a single table
// so all three call sites make the same decision.
function stripCanvasOverlays(canvas: Canvas, mode: EExportRenderingMode): void {
    const { keepCuttingGuides } = _OPTIONS_BY_MODE[mode];

    _hideSelectionHandles(canvas);
    _removeById(canvas, GRID_HELPER_ID);
    // A mask zone is not printed: its effect is already in the clips of the objects under it.
    canvas
        .getObjects()
        .filter(isMaskImage)
        .forEach((mask) => canvas.remove(mask));

    if (keepCuttingGuides) {
        setCuttingPathVisibility(canvas, CUTTING_PATH_DEFAULT_OPACITY);
        setBackgroundMaskVisibility(canvas, BACKGROUND_MASK_DEFAULT_OPACITY);
    } else {
        _removeById(canvas, CUTTING_SHAPE_ID);
        _removeGroupById(canvas, CUTTING_OUTLINE_NAME);
    }
}

function _hideSelectionHandles(canvas: Canvas): void {
    const activeObject = canvas.getActiveObject();
    if (!activeObject) {
        return;
    }
    activeObject.set({ hasControls: false, hasBorders: false });
}

function _removeById(canvas: Canvas, id: string): void {
    const matches = canvas.getObjects().filter((obj) => (obj as FabricObject & { id?: string }).id === id);
    matches.forEach((obj) => canvas.remove(obj));
}

function _removeGroupById(canvas: Canvas, id: string): void {
    const match = canvas.getObjects('group').find((obj) => (obj as FabricObject & { id?: string }).id === id);
    if (match) {
        canvas.remove(match);
    }
}

export { stripCanvasOverlays };
